import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, FileText } from 'lucide-react';
import { useIFCarteiraResumo } from '../hooks/useIFCarteira';
import { EmpMedicoesView } from '@/components/empreendimentos/EmpMedicoesView';
import { PageHeader } from '@/components/system/PageHeader';
import { SectionCard } from '@/components/system/SectionCard';
import { EmptyState } from '@/components/system/EmptyState';
import { FilterBar } from '@/components/system/FilterBar';
import { DataTable, type Column } from '@/components/system/DataTable';
import { Badge } from '@/components/ui/badge';
import type { IFContratoResumo } from '../types/financiamento.types';

const IFMedicoesPage: React.FC = () => {
  const navigate = useNavigate();
  const { resumos, isLoading } = useIFCarteiraResumo();
  const [selectedContratoId, setSelectedContratoId] = useState<string | null>(null);

  if (isLoading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  const selectedContrato = resumos.find(r => r.contrato_id === selectedContratoId);

  const summaryColumns: Column<IFContratoResumo>[] = [
    { key: 'contrato', label: 'Contrato', render: r => <span className="font-medium">{r.contrato_nome}</span> },
    { key: 'contratada', label: 'Contratada', render: r => <span className="text-sm text-muted-foreground">{r.contratada}</span> },
    { key: 'ultimo', label: 'Último Boletim', align: 'center', render: r => r.ultimo_boletim_numero ? <Badge variant="outline" className="text-xs">#{r.ultimo_boletim_numero}</Badge> : <span>—</span> },
    { key: 'progresso', label: 'Progresso', align: 'center', render: r => <span className="text-sm font-medium">{r.progresso_fisico_pct.toFixed(1)}%</span> },
  ];

  return (
    <div className="space-y-6">
        <PageHeader
          title="Medições e Execução"
          subtitle="Boletins de medição dos contratos monitorados — visualização somente leitura"
        />

        <FilterBar>
          <label className="text-sm font-medium text-muted-foreground whitespace-nowrap">Contrato:</label>
          <Select value={selectedContratoId || ''} onValueChange={v => setSelectedContratoId(v)}>
            <SelectTrigger className="max-w-md h-8 text-sm">
              <SelectValue placeholder="Selecione um contrato monitorado" />
            </SelectTrigger>
            <SelectContent>
              {resumos.map(r => (
                <SelectItem key={r.contrato_id} value={r.contrato_id}>
                  {r.contrato_nome} — {r.contratada}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterBar>

        {!selectedContratoId ? (
          <SectionCard title="Resumo de Medições" icon={FileText}>
            {resumos.length === 0 ? (
              <EmptyState text="Nenhum contrato vinculado à carteira de monitoramento." icon={FileText} />
            ) : (
              <DataTable
                columns={summaryColumns}
                data={resumos}
                keyExtractor={r => r.contrato_id}
                emptyText="Nenhum contrato disponível."
                onRowClick={r => setSelectedContratoId(r.contrato_id)}
              />
            )}
          </SectionCard>
        ) : (
          <EmpMedicoesView
            contratoId={selectedContratoId}
            contratoNome={selectedContrato?.contrato_nome || ''}
            canEdit={false}
          />
        )}
    </div>
  );
};

export default IFMedicoesPage;
